import React from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";
import MarbleImage from "./MarbleImage";

/**
 * Lot card component for Type pages (Phase B - live data).
 * Shows lot summary with click-through to Lot page.
 * Cover image is forced landscape (16:9) regardless of source image dimensions.
 * Only Thickness is shown as a data row (price stays hidden per business decision -
 * see "Request Price" button on the Lot page for the price-reveal flow).
 */
const LotCard = ({ lot, typeSlug, revealDelay = 0 }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/collection/${typeSlug}/lot/${lot.lot_sno}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Reveal delay={revealDelay}>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-testid={`lot-card-${lot.lot_sno}`}
        aria-label={`View ${lot.lot_number}`}
        className="group relative cursor-pointer overflow-hidden bg-brand-surface ring-1 ring-white/5 transition-all duration-500 hover:ring-brand-gold/45 focus-visible:ring-brand-gold/60 focus-visible:outline-none"
      >
        {/* Cover Image - enforced landscape 16:9 */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <MarbleImage
            src={lot.image_url}
            alt={lot.lot_number}
            size="thumb"
            wrapperClassName="absolute inset-0"
            className="transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/20 to-transparent" />
        </div>

        {/* Lot Details */}
        <div className="relative bg-brand-charcoal/95 px-5 md:px-6 py-5 md:py-6">
          {/* Lot Number */}
          <h3
            data-testid={`lot-card-number-${lot.lot_sno}`}
            className="font-serif text-xl md:text-2xl font-light text-brand-cream leading-none mb-4"
          >
            {lot.lot_number}
          </h3>

          {/* Details Row */}
          <div className="space-y-2.5 mb-5">
            <div className="flex justify-between items-baseline border-b border-white/5 pb-2">
              <span className="font-sans text-[10px] uppercase tracking-[0.24em] text-brand-cream/45">
                Thickness
              </span>
              <span className="font-sans text-sm text-brand-cream/75">
                {lot.thickness || "-"}
              </span>
            </div>
          </div>

          {/* CTA: View Slabs */}
          <div className="flex items-center justify-end gap-2 text-brand-gold group-hover:text-brand-gold/90 transition-colors">
            <span className="font-sans text-sm uppercase tracking-[0.24em]">
              View Slabs
            </span>
            <span className="text-lg">→</span>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export default LotCard;
